// Backend connection probe — long-lived WebSocket to /api/ws/ping. The
// server is configured not to log the ping traffic so the cadence is
// invisible in the request log. The socket carries a single text
// protocol: client sends "ping" every PING_INTERVAL_MS, server replies
// with "pong"; the indicator goes red the moment we miss a pong by
// more than PONG_TIMEOUT_MS, or the socket closes.
//
// Factory + injectable socket-maker (same idiom as the calendar
// controllers) so the reconnect/backoff/timeout machine is testable
// with fake timers; the module exports a singleton built over the
// real WebSocket for App/StatusBar.

import { createSignal, type Accessor } from "solid-js";

export type ConnectionState = "checking" | "connected" | "disconnected";

const PING_INTERVAL_MS = 5000;
const PONG_TIMEOUT_MS = 4000;   // budget for the round trip after a ping
const CONNECT_TIMEOUT_MS = 5000; // give up on "Checking…" after this
export const RECONNECT_MIN_MS = 1000;
export const RECONNECT_MAX_MS = 30_000;

// The subset of WebSocket the poller touches — lets tests hand in a
// hand-rolled fake instead of a network socket.
export interface PollerSocket {
  readyState: number;
  send(data: string): void;
  close(): void;
  addEventListener(type: "open" | "message" | "close" | "error", fn: (ev: { data?: unknown }) => void): void;
}

export interface ConnectionPoller {
  status: Accessor<ConnectionState>;
  lastChecked: Accessor<Date | null>;
  start(): void;
  stop(): void;
  ping(): void;
}

export function createConnectionPoller(makeSocket: () => PollerSocket): ConnectionPoller {
  const [status, setStatusInternal] = createSignal<ConnectionState>("checking");
  const [lastChecked, setLastChecked] = createSignal<Date | null>(null);

  let socket: PollerSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  let connectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = RECONNECT_MIN_MS;
  let stopped = true;

  function clearTimers(): void {
    if (pingTimer != null) { clearInterval(pingTimer); pingTimer = null; }
    if (pongTimer != null) { clearTimeout(pongTimer); pongTimer = null; }
    if (connectTimer != null) { clearTimeout(connectTimer); connectTimer = null; }
  }

  function scheduleReconnect(): void {
    if (stopped) return;
    if (reconnectTimer != null) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openSocket();
    }, reconnectDelay);
    // Exponential backoff capped at RECONNECT_MAX_MS so we don't hammer
    // a server that's genuinely down. Reset to RECONNECT_MIN_MS on a
    // successful open.
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
  }

  function markDisconnected(): void {
    setStatusInternal("disconnected");
    setLastChecked(new Date());
  }

  function sendPing(): void {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send("ping");
    } catch {
      // Socket failed mid-send — let onclose / onerror flip status.
      return;
    }
    // Arm the timeout — if no pong arrives in PONG_TIMEOUT_MS we treat
    // the connection as dead and force a reconnect. Each ping cancels
    // the previous timer so a quick succession of ping/pong doesn't
    // false-trigger. close() is safe here because the socket is OPEN
    // (the no-pong path implies the wire is dead but the readyState is
    // still 1) — the connection-established warning only fires when
    // close() is called in CONNECTING state.
    if (pongTimer != null) clearTimeout(pongTimer);
    pongTimer = setTimeout(() => {
      pongTimer = null;
      markDisconnected();
      try { socket?.close(); } catch { /* already closing */ }
    }, PONG_TIMEOUT_MS);
  }

  function openSocket(): void {
    if (stopped) return;
    let ws: PollerSocket;
    try {
      ws = makeSocket();
    } catch {
      markDisconnected();
      scheduleReconnect();
      return;
    }
    socket = ws;
    setStatusInternal("checking");

    // Connect-timeout: if the WS sits in CONNECTING for longer than
    // CONNECT_TIMEOUT_MS without firing open / close (e.g., the proxy
    // accepted the TCP connection but never completed the upgrade
    // handshake), abandon our reference and schedule a reconnect. We
    // intentionally do NOT call `ws.close()` here — calling close() on
    // a CONNECTING socket makes Chromium log "WebSocket is closed
    // before the connection is established" on every retry, drowning
    // the dev console. The orphan WS gets GC'd once the browser's own
    // connect timeout expires (~60 s); our handlers ignore it because
    // socket !== ws by then.
    connectTimer = setTimeout(() => {
      connectTimer = null;
      if (ws !== socket || ws.readyState === WebSocket.OPEN) return;
      socket = null;
      markDisconnected();
      scheduleReconnect();
    }, CONNECT_TIMEOUT_MS);

    ws.addEventListener("open", () => {
      if (ws !== socket) return;
      if (connectTimer != null) { clearTimeout(connectTimer); connectTimer = null; }
      reconnectDelay = RECONNECT_MIN_MS;
      setStatusInternal("connected");
      setLastChecked(new Date());
      // First ping immediately so the round-trip time is recorded;
      // subsequent pings fire every PING_INTERVAL_MS.
      sendPing();
      pingTimer = setInterval(sendPing, PING_INTERVAL_MS);
    });

    ws.addEventListener("message", (ev) => {
      if (ws !== socket) return;
      if (ev.data !== "pong") return;
      if (pongTimer != null) { clearTimeout(pongTimer); pongTimer = null; }
      setStatusInternal("connected");
      setLastChecked(new Date());
    });

    ws.addEventListener("close", () => {
      if (ws !== socket) return;
      socket = null;
      clearTimers();
      markDisconnected();
      scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // The browser will fire `close` right after; let that handler do
      // the reconnect bookkeeping so we don't double-schedule.
    });
  }

  return {
    status,
    lastChecked,
    start(): void {
      if (!stopped) return;
      stopped = false;
      reconnectDelay = RECONNECT_MIN_MS;
      openSocket();
    },
    stop(): void {
      stopped = true;
      clearTimers();
      if (reconnectTimer != null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (socket) {
        try { socket.close(); } catch { /* already closing */ }
        socket = null;
      }
    },
    // One-shot manual ping, e.g. from a "retry now" button. Idempotent —
    // reuses the existing socket if open, otherwise kicks a reconnect.
    ping(): void {
      if (socket && socket.readyState === WebSocket.OPEN) {
        sendPing();
        return;
      }
      if (stopped) this.start();
      else openSocket();
    },
  };
}

// --- App-facing singleton over the real WebSocket ---

function makeRealSocket(): PollerSocket {
  // Vite dev: the page is on :5173 but the backend is on :8080. Vite's
  // /api proxy supports HTTP fine, but its WS proxying is flaky on
  // some versions / configs — point the WS straight at the backend
  // instead. Prod: page and backend are on the same origin (Bun
  // serves both the bundle and the WS), so window.location works.
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const host = import.meta.env.DEV ? "localhost:8080" : window.location.host;
  return new WebSocket(`${proto}://${host}/api/ws/ping`);
}

let singleton: ConnectionPoller | null = null;
function poller(): ConnectionPoller {
  return (singleton ??= createConnectionPoller(makeRealSocket));
}

export function connectionStatus(): ConnectionState {
  return poller().status();
}
export function connectionLastChecked(): Date | null {
  return poller().lastChecked();
}
export function startConnectionPolling(): void {
  poller().start();
}
export function stopConnectionPolling(): void {
  poller().stop();
}
export async function pingBackend(): Promise<void> {
  poller().ping();
}
